/**
 * Q3 — N2 · Competência 3 (Geometria) · task-models H2.13.K01 a K04
 *
 *   Ângulos de polígonos convexos, em quatro habilidades:
 *     K01 — soma dos ângulos internos;
 *     K02 — medida de cada ângulo interno de um polígono regular;
 *     K03 — soma dos ângulos externos;
 *     K04 — medida de cada ângulo externo de um polígono regular.
 *
 * ADAPTADO DA QUESTÃO 03 do material no GeoGebra. Uma versão anterior juntava as
 * quatro perguntas num item só, com a mesma animação para todas. Separadas, cada uma
 * ganha o SUPORTE que serve a ela:
 *
 *  - INTERNOS (K01, K02): um EXPLORADOR, como o das diagonais. O aluno muda o número de
 *    lados, de 3 a 8, e vê a divisão em triângulos (K01) ou o ângulo de cada vértice do
 *    polígono regular (K02). A pergunta é sobre um polígono FORA desse alcance: o
 *    explorador ensina o padrão, a resposta pede a generalização.
 *  - EXTERNOS (K03, K04): uma animação com triângulo, quadrilátero e pentágono ao mesmo
 *    tempo, encolhendo até os ângulos externos de cada um fecharem um círculo. Três
 *    polígonos diferentes, a mesma volta: é essa comparação que convence.
 *
 * Os ERROS são os mesmos solucionadores defeituosos nos quatro itens — contar um
 * triângulo por lado, confundir interno com externo, dar a soma quando se pedia cada
 * ângulo —, cada um devolvendo NOT_APPLICABLE onde não cabe.
 *
 * ALTERNATIVAS EMBARALHADAS, e não em ordem crescente. Com a pergunta fixa, os erros
 * típicos caem sempre dos mesmos lados da chave: em ordem crescente, a chave dos
 * externos seria sempre a segunda alternativa, e a dos internos alternaria entre só
 * duas letras. Um aluno aprenderia a posição, não o ângulo.
 */

import { formatBR } from '../../core/num.ts';
import type { Rng } from '../../core/rng.ts';
import { assembleSelectOne } from '../../engine/assemble.ts';
import { comExplicacoes, type Explicacoes } from '../../engine/explain.ts';
import {
  animacaoExternos,
  exploradorAnguloInterno,
  exploradorSomaInternos,
} from '../../render/angulos.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  ANG_DIVIDIU_180,
  ANG_DOIS_EXTERNOS,
  ANG_EXT_COMO_INT,
  ANG_EXT_RETO,
  ANG_INT_COMO_EXT,
  ANG_N_MENOS_1,
  ANG_N_MENOS_3,
  ANG_SEM_SUBTRAIR,
  ANG_SOMA_PARES,
  ANG_SOMA_POR_UM,
  ANG_TRIANGULO,
} from '../../schema/errors.ts';
import type { BuggySolver, ItemTemplate, Maybe, Stimulus } from '../../schema/item.ts';
import { NOT_APPLICABLE, templateId } from '../../schema/item.ts';
import { br, strong, txt, visibleLength, type RichText } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

export type Tipo = 'soma-int' | 'int-cada' | 'soma-ext' | 'ext-cada';

interface Cenario {
  readonly texto: string;
  readonly pergunta: string;
}

/**
 * Os externos ganham contextos de GIRO — um robô, um ciclista, uma tartaruga de
 * programa de desenho —, porque é neles que o ângulo externo tem sentido físico. E o
 * comando diz com todas as letras que giro é esse: o que corrige a trajetória em cada
 * vértice para seguir pelo lado seguinte.
 */
const CENARIOS: Readonly<Record<Tipo, readonly Cenario[]>> = {
  'soma-int': [
    {
      texto:
        'Um terreno tem a forma de um polígono convexo de {n} lados. Um topógrafo mediu cada um dos ângulos internos do terreno e somou as medidas.',
      pergunta: 'Qual é o valor dessa soma?',
    },
    {
      texto:
        'Um artesão recortou uma peça de madeira com a forma de um polígono convexo de {n} lados.',
      pergunta: 'Qual é a soma das medidas dos ângulos internos dessa peça?',
    },
    {
      texto: 'O contorno de uma praça forma um polígono convexo de {n} lados.',
      pergunta: 'Qual é a soma das medidas dos ângulos internos desse polígono?',
    },
  ],
  'int-cada': [
    {
      texto: 'O tampo de uma mesa tem a forma de um polígono regular de {n} lados.',
      pergunta: 'Qual é a medida de cada ângulo interno do tampo?',
    },
    {
      texto: 'Um mosaico é montado com peças que têm a forma de um polígono regular de {n} lados.',
      pergunta: 'Quanto mede cada ângulo interno de uma dessas peças?',
    },
    {
      texto: 'A moldura de um espelho tem a forma de um polígono regular de {n} lados.',
      pergunta: 'Qual é a medida de cada ângulo interno da moldura?',
    },
  ],
  'soma-ext': [
    {
      texto:
        'Um robô aspirador percorre o contorno de uma sala com a forma de um polígono convexo de {n} lados. Ao chegar a cada vértice, ele precisa girar para corrigir sua trajetória e seguir pelo lado seguinte; esse giro é o ângulo externo do polígono naquele vértice.',
      pergunta:
        'Ao completar uma volta e voltar à posição de partida, quantos graus o robô terá girado no total, somando os giros de todos os vértices?',
    },
    {
      texto:
        'Um ciclista contorna uma pista com a forma de um polígono convexo de {n} lados. Em cada vértice, ele precisa virar o guidão para corrigir sua trajetória e seguir pelo lado seguinte; cada virada corresponde ao ângulo externo naquele vértice.',
      pergunta:
        'Somando as viradas de todos os vértices, quantos graus o ciclista terá virado ao completar uma volta na pista?',
    },
    {
      texto: 'Um polígono convexo tem {n} lados.',
      // O comando nomeia o polígono: entre ele e o enunciado entra a animação, com
      // TRÊS polígonos, e "os seus ângulos externos" perdia o antecedente.
      pergunta:
        'Qual é a soma das medidas dos ângulos externos do polígono de {n} lados, um em cada vértice?',
    },
  ],
  'ext-cada': [
    {
      texto:
        'Um robô percorre o contorno de uma pista com a forma de um polígono regular de {n} lados. Em cada vértice, ele precisa girar para corrigir sua trajetória e seguir pelo lado seguinte — e, como o polígono é regular, o giro é o mesmo em todos os vértices.',
      pergunta: 'Quantos graus o robô deve girar em cada vértice para corrigir sua trajetória?',
    },
    {
      texto:
        'Num programa de desenho, uma tartaruga desenha um polígono regular de {n} lados: anda um lado e, ao chegar ao vértice, gira para seguir pelo lado seguinte, até voltar ao ponto de partida.',
      pergunta: 'Quantos graus a tartaruga deve girar em cada vértice?',
    },
    {
      texto: 'Uma placa de trânsito tem a forma de um polígono regular de {n} lados.',
      pergunta: 'Qual é a medida de cada ângulo externo desse polígono?',
    },
  ],
};

/** Alcance do seletor dos exploradores — sempre abaixo do polígono perguntado. */
export const EXPLORADOR_MIN = 3;
export const EXPLORADOR_MAX = 8;

/** Lados sorteáveis em cada pergunta: fora do explorador e com ângulos inteiros. */
const LADOS: Readonly<Record<Tipo, readonly number[]>> = {
  'soma-int': [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
  'int-cada': [9, 10, 12, 15, 18, 20],
  'soma-ext': [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 18, 20],
  'ext-cada': [6, 8, 9, 10, 12, 15, 18, 20],
};

export interface Params {
  readonly tipo: Tipo;
  readonly n: number;
  readonly cenario: number;
}

const soma = (n: number): number => (n - 2) * 180;

export function solveKey(p: Params): number {
  switch (p.tipo) {
    case 'soma-int':
      return soma(p.n);
    case 'soma-ext':
      return 360;
    case 'int-cada':
      return soma(p.n) / p.n;
    case 'ext-cada':
      return 360 / p.n;
  }
}

// ---------------------------------------------------------------------------
// Solucionadores defeituosos
// ---------------------------------------------------------------------------

const g = (x: number): string => `${formatBR(x, 0)}°`;
const render = (v: number): RichText => [txt(g(v))];

/**
 * TEXTOS DE COMPRIMENTO PARECIDO: a alternativa mais longa não passa de 1,6 vez a mais
 * curta ("18°" ao lado de "3.240°" denunciaria a intrusa pelo tamanho). Um erro cujo
 * texto sai da faixa da pergunta não é oferecido — é NOT_APPLICABLE.
 */
const FAIXA: Readonly<Record<Tipo, readonly [number, number]>> = {
  'soma-int': [4, 6],
  'soma-ext': [4, 6],
  'int-cada': [3, 4],
  'ext-cada': [3, 4],
};

function naFaixa(p: Params, v: number): Maybe<number> {
  if (!Number.isInteger(v) || v <= 0) return NOT_APPLICABLE;
  const [lo, hi] = FAIXA[p.tipo];
  const len = visibleLength(render(v));
  return len >= lo && len <= hi ? v : NOT_APPLICABLE;
}

const por =
  (f: Partial<Record<Tipo, (p: Params) => number>>) =>
  (p: Params): Maybe<number> => {
    const fn = f[p.tipo];
    return fn ? naFaixa(p, fn(p)) : NOT_APPLICABLE;
  };

const BUGGY: readonly BuggySolver<Params, number>[] = [
  {
    error: ANG_SEM_SUBTRAIR,
    priority: 1,
    solve: por({ 'soma-int': (p) => p.n * 180, 'int-cada': () => 180 }),
  },
  {
    error: ANG_EXT_COMO_INT,
    priority: 1,
    solve: por({ 'soma-ext': (p) => soma(p.n), 'ext-cada': (p) => soma(p.n) / p.n }),
  },
  {
    error: ANG_SOMA_POR_UM,
    priority: 1,
    solve: por({ 'int-cada': (p) => soma(p.n), 'ext-cada': () => 360 }),
  },
  {
    error: ANG_TRIANGULO,
    priority: 1,
    solve: por({ 'soma-int': () => 180, 'soma-ext': () => 180, 'ext-cada': (p) => 180 / p.n }),
  },
  {
    error: ANG_INT_COMO_EXT,
    priority: 2,
    solve: por({ 'soma-int': () => 360, 'int-cada': (p) => 360 / p.n }),
  },
  {
    error: ANG_N_MENOS_1,
    priority: 2,
    solve: por({ 'soma-int': (p) => (p.n - 1) * 180, 'int-cada': (p) => ((p.n - 1) * 180) / p.n }),
  },
  {
    error: ANG_N_MENOS_3,
    priority: 2,
    solve: por({ 'soma-int': (p) => (p.n - 3) * 180, 'int-cada': (p) => ((p.n - 3) * 180) / p.n }),
  },
  {
    error: ANG_SOMA_PARES,
    priority: 2,
    solve: por({ 'soma-ext': (p) => p.n * 180, 'ext-cada': () => 180 }),
  },
  {
    error: ANG_DOIS_EXTERNOS,
    priority: 2,
    solve: por({ 'soma-ext': () => 720, 'ext-cada': (p) => 720 / p.n }),
  },
  {
    error: ANG_EXT_RETO,
    priority: 2,
    solve: por({ 'soma-ext': (p) => p.n * 90, 'ext-cada': () => 90 }),
  },
  { error: ANG_DIVIDIU_180, priority: 3, solve: por({ 'int-cada': (p) => 180 / p.n }) },
];

const EXPLICA: Explicacoes<Params, number> = {
  [ANG_SEM_SUBTRAIR]: (p, v) =>
    p.tipo === 'soma-int'
      ? [
          txt(
            `Você fez ${p.n} × 180° = ${g(v)}, como se o polígono se dividisse em um triângulo por lado. `,
          ),
          txt(
            `Traçando as diagonais a partir de UM vértice — como no explorador —, ele se divide em ${p.n} − 2 = ${p.n - 2} triângulos.`,
          ),
        ]
      : [
          txt(
            `${g(v)} seria a medida de cada ângulo se o polígono se dividisse em ${p.n} triângulos, um por lado. `,
          ),
          txt(
            `São ${p.n} − 2 = ${p.n - 2} triângulos: a soma é ${g(soma(p.n))}, e cada ângulo mede ${g(soma(p.n))} ÷ ${p.n} = ${g(solveKey(p))}.`,
          ),
        ],
  [ANG_N_MENOS_1]: (p, v) => [
    txt(
      p.tipo === 'soma-int'
        ? `Você fez (${p.n} − 1) × 180° = ${g(v)}. `
        : `Você fez (${p.n} − 1) × 180° ÷ ${p.n} = ${g(v)}. `,
    ),
    txt(
      `As diagonais de um vértice dividem o polígono em ${p.n} − 2 = ${p.n - 2} triângulos, e não em ${p.n - 1}.`,
    ),
  ],
  [ANG_N_MENOS_3]: (p, v) => [
    txt(
      p.tipo === 'soma-int'
        ? `Você fez (${p.n} − 3) × 180° = ${g(v)}. `
        : `Você fez (${p.n} − 3) × 180° ÷ ${p.n} = ${g(v)}. `,
    ),
    txt(
      `${p.n} − 3 = ${p.n - 3} é o número de diagonais que saem de um vértice; elas dividem o polígono em ${p.n - 3} + 1 = ${p.n - 2} triângulos.`,
    ),
  ],
  [ANG_INT_COMO_EXT]: (p, v) =>
    p.tipo === 'soma-int'
      ? [
          txt(`${g(v)} é a soma dos ângulos EXTERNOS — uma volta completa. `),
          txt(`A pergunta é sobre os internos: (${p.n} − 2) × 180° = ${g(solveKey(p))}.`),
        ]
      : [
          txt(`360° ÷ ${p.n} = ${g(v)} é a medida de cada ângulo EXTERNO. `),
          txt(`O interno é o que falta para 180°: 180° − ${g(v)} = ${g(solveKey(p))}.`),
        ],
  [ANG_EXT_COMO_INT]: (p, v) =>
    p.tipo === 'soma-ext'
      ? [
          txt(`(${p.n} − 2) × 180° = ${g(v)} é a soma dos ângulos INTERNOS. `),
          txt(
            'Os externos, somados, dão sempre uma volta completa: 360°, qualquer que seja o número de lados — como na animação.',
          ),
        ]
      : [
          txt(`${g(v)} é a medida de cada ângulo INTERNO. `),
          txt(
            `O giro em cada vértice é o que falta para 180°: 180° − ${g(v)} = ${g(solveKey(p))}.`,
          ),
        ],
  [ANG_SOMA_PARES]: (p, v) =>
    p.tipo === 'soma-ext'
      ? [
          txt(`Você fez ${p.n} × 180° = ${g(v)}: em cada vértice, interno + externo = 180°. `),
          txt(
            `Essa conta soma internos E externos. Tirando os internos, (${p.n} − 2) × 180°, sobram 2 × 180° = 360°.`,
          ),
        ]
      : [
          txt(
            `${g(v)} é a soma do ângulo interno com o externo, em cada vértice — não o giro sozinho, `,
          ),
          txt(`que mede 360° ÷ ${p.n} = ${g(solveKey(p))}.`),
        ],
  [ANG_TRIANGULO]: (p, v) =>
    p.tipo === 'ext-cada'
      ? [
          txt(`Você fez 180° ÷ ${p.n} = ${g(v)}, partindo da soma dos ângulos do triângulo. `),
          txt(
            `Os giros de uma volta somam 360°, e cada um mede 360° ÷ ${p.n} = ${g(solveKey(p))}.`,
          ),
        ]
      : [
          txt(
            `${g(v)} é a soma dos ângulos internos de um TRIÂNGULO, e ela não vale para qualquer polígono. `,
          ),
          txt(
            p.tipo === 'soma-int'
              ? `Com ${p.n} lados, são ${p.n - 2} triângulos: ${p.n - 2} × 180° = ${g(solveKey(p))}.`
              : 'Os externos de qualquer polígono convexo somam uma volta completa: 360°.',
          ),
        ],
  [ANG_DOIS_EXTERNOS]: (p, v) =>
    p.tipo === 'soma-ext'
      ? [
          txt(
            `${g(v)} são duas voltas completas — o que se obtém contando os dois ângulos externos de cada vértice. `,
          ),
          txt('A soma considera um externo por vértice: uma volta, 360°.'),
        ]
      : [
          txt(`Você fez 720° ÷ ${p.n} = ${g(v)}, contando duas voltas. `),
          txt(`Numa volta, cada giro mede 360° ÷ ${p.n} = ${g(solveKey(p))}.`),
        ],
  [ANG_EXT_RETO]: (p, v) =>
    p.tipo === 'soma-ext'
      ? [
          txt(`Você fez ${p.n} × 90° = ${g(v)}, contando um giro reto em cada vértice. `),
          txt(
            'Só no retângulo os giros são retos. Nos outros polígonos eles se ajustam, e a soma é sempre uma volta: 360°.',
          ),
        ]
      : [
          txt(`${g(v)} é o giro em cada canto de um retângulo. `),
          txt(
            `Com ${p.n} lados iguais, a volta de 360° se reparte em ${p.n} giros de ${g(solveKey(p))}.`,
          ),
        ],
  [ANG_SOMA_POR_UM]: (p, v) =>
    p.tipo === 'int-cada'
      ? [
          txt(`(${p.n} − 2) × 180° = ${g(v)} é a soma de TODOS os ângulos internos. `),
          txt(
            `Num polígono regular eles são iguais: cada um mede ${g(v)} ÷ ${p.n} = ${g(solveKey(p))}.`,
          ),
        ]
      : [
          txt(`${g(v)} é a soma de todos os giros — a volta inteira. `),
          txt(`Em cada vértice, o giro é ${g(v)} ÷ ${p.n} = ${g(solveKey(p))}.`),
        ],
  [ANG_DIVIDIU_180]: (p, v) => [
    txt(`Você fez 180° ÷ ${p.n} = ${g(v)}. `),
    txt(
      `A soma dos internos só é 180° no triângulo; com ${p.n} lados, ela é (${p.n} − 2) × 180° = ${g(soma(p.n))}, e cada ângulo mede ${g(solveKey(p))}.`,
    ),
  ],
};

// ---------------------------------------------------------------------------
// Resolução
// ---------------------------------------------------------------------------

function resolucao(p: Params, key: number): RichText {
  const n = p.n;
  const inicio: RichText = [strong('Resolução. ')];
  switch (p.tipo) {
    case 'soma-int':
      return [
        ...inicio,
        txt(
          `As diagonais que saem de um vértice dividem o polígono de ${n} lados em ${n} − 2 = ${n - 2} triângulos.`,
        ),
        br,
        txt(
          `Os ângulos desses triângulos formam, juntos, os ângulos internos do polígono: ${n - 2} × 180° = ${g(key)}.`,
        ),
      ];
    case 'soma-ext':
      return [
        ...inicio,
        txt(
          'Em cada vértice, interno e externo somam 180°. Com n vértices, isso dá n × 180°; tirando a soma dos internos, (n − 2) × 180°, sobram 2 × 180° = 360°.',
        ),
        br,
        txt(
          `Não depende do número de lados: com ${n} lados, a soma dos externos também é 360° — a volta completa que a animação mostra nos três polígonos.`,
        ),
      ];
    case 'int-cada':
      return [
        ...inicio,
        txt(`A soma dos internos é (${n} − 2) × 180° = ${g(soma(n))}.`),
        br,
        txt(`No polígono regular, os ${n} ângulos são iguais: ${g(soma(n))} ÷ ${n} = ${g(key)}.`),
        br,
        txt(
          `Conferindo pelo externo: cada externo mede 360° ÷ ${n} = ${g(360 / n)}, e 180° − ${g(360 / n)} = ${g(key)}.`,
        ),
      ];
    case 'ext-cada':
      return [
        ...inicio,
        txt(
          `Numa volta completa, os giros somam 360°. No polígono regular de ${n} lados, eles são iguais: 360° ÷ ${n} = ${g(key)}.`,
        ),
        br,
        txt(
          `Conferindo: o interno mede (${n} − 2) × 180° ÷ ${n} = ${g(soma(n) / n)}, e ${g(soma(n) / n)} + ${g(key)} = 180°.`,
        ),
      ];
  }
}

// ---------------------------------------------------------------------------
// Suportes
// ---------------------------------------------------------------------------

function suporte(p: Params): { readonly estimulo: Stimulus; readonly convite: RichText } {
  const a11y = (title: string, desc: string) => ({ title, desc });
  if (p.tipo === 'soma-int' || p.tipo === 'int-cada') {
    const soma1 = p.tipo === 'soma-int';
    const alt = soma1
      ? `Explorador interativo: um polígono convexo, com o número de lados escolhido num seletor de ${EXPLORADOR_MIN} a ${EXPLORADOR_MAX}, ` +
        'dividido em triângulos pelas diagonais que partem de um vértice; cada triângulo mostra 180°, e a soma dos ângulos internos é informada. ' +
        `O polígono da pergunta, com ${p.n} lados, está fora do alcance do seletor.`
      : `Explorador interativo: um polígono regular, com o número de lados escolhido num seletor de ${EXPLORADOR_MIN} a ${EXPLORADOR_MAX}, ` +
        'com os ângulos internos marcados; a soma dos ângulos internos e a medida de cada um são informadas. ' +
        `O polígono da pergunta, com ${p.n} lados, está fora do alcance do seletor.`;
    return {
      estimulo: {
        kind: 'manipulable',
        motion: 'interactive',
        audio: { need: 'none' },
        id: 'explorador',
        alt,
        controls: [
          {
            id: 'lados',
            label: [txt('Lados do polígono')],
            min: EXPLORADOR_MIN,
            max: EXPLORADOR_MAX,
            step: 1,
            initial: 4,
            format: (v) => `${formatBR(v, 0)} lados`,
          },
        ],
        frame: (valores) => {
          const k = Math.round(valores['lados'] ?? 4);
          return soma1
            ? exploradorSomaInternos(k, a11y(`Polígono de ${k} lados dividido em triângulos`, alt))
            : exploradorAnguloInterno(k, a11y(`Polígono regular de ${k} lados`, alt));
        },
      },
      convite: soma1
        ? [
            // Molde do autor (2026-09-29): "Utilize o explorador abaixo para …. Procure
            // identificar um padrão." — no lugar de "Antes de responder, use …: …".
            txt('Utilize o explorador abaixo para observar em quantos '),
            strong('triângulos'),
            txt(
              ' as diagonais que partem de um vértice dividem o polígono, conforme o número de lados muda. Procure identificar um padrão para a soma dos ângulos internos.',
            ),
          ]
        : [
            // Redação do autor, literal (2026-09-29).
            txt(
              'Utilize o explorador abaixo para avaliar a medida de um ângulo interno e a soma dos ângulos internos de polígonos regulares de 3 a 8 lados. Procure identificar um padrão.',
            ),
          ],
    };
  }
  const alt =
    'Animação sem som com três polígonos convexos irregulares lado a lado: um triângulo, um quadrilátero e um pentágono. ' +
    'Em cada vértice, o lado é prolongado e o ângulo externo — o giro até o lado seguinte — é pintado. Os três polígonos ' +
    'se contraem até virar um ponto, e os ângulos externos, que não mudam de tamanho, se juntam em torno desse ponto. ' +
    'Acima da animação, a frase: "Repare que os ângulos externos de cada polígono não se alteram quando ele se contrai."';
  return {
    estimulo: {
      kind: 'animated',
      motion: 'animated',
      audio: { need: 'none' },
      id: 'animacao',
      alt,
      durationMs: 9000,
      loop: true,
      // Sob `prefers-reduced-motion`, o quadro final: as três voltas completas.
      staticFallbackAt: 0.97,
      frame: (t) => animacaoExternos(t, a11y('Ângulos externos de três polígonos', alt)),
    },
    convite: [
      txt('A animação abaixo mostra o que acontece com os ângulos '),
      strong('externos'),
      txt(' de três polígonos diferentes quando eles encolhem.'),
    ],
  };
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

interface Ficha {
  readonly id: string;
  readonly codigo: string;
  readonly titulo: string;
  readonly fontes: readonly DifficultySourceCode[];
}

const FICHAS: Readonly<Record<Tipo, Ficha>> = {
  'soma-int': {
    id: 'h2-13-K01-soma-internos',
    codigo: 'H2.13.K01',
    titulo: 'Soma dos ângulos internos de um polígono',
    fontes: ['steps.2'],
  },
  'int-cada': {
    id: 'h2-13-K02-angulo-interno',
    codigo: 'H2.13.K02',
    titulo: 'Ângulo interno de um polígono regular',
    fontes: ['steps.3plus'],
  },
  'soma-ext': {
    id: 'h2-13-K03-soma-externos',
    codigo: 'H2.13.K03',
    titulo: 'Soma dos ângulos externos de um polígono',
    fontes: ['steps.1'],
  },
  'ext-cada': {
    id: 'h2-13-K04-angulo-externo',
    codigo: 'H2.13.K04',
    titulo: 'Ângulo externo de um polígono regular',
    fontes: ['steps.2'],
  },
};

function criar(tipo: Tipo): ItemTemplate<Params, number> {
  const f = FICHAS[tipo];
  return {
    id: templateId(f.id),
    // v2: explorador sem legenda; animação com os externos já traçados e texto fixo.
    version: 2,
    taskModel: taskModelCode(f.codigo),
    skills: [skillCode('H2.13')],
    axis: 'II',
    level: 'N2',
    title: f.titulo,
    responseModel: '3PL',

    difficultySources: f.fontes,

    params: (rng: Rng): Params => ({
      tipo,
      n: rng.pick(LADOS[tipo]),
      cenario: rng.int(0, CENARIOS[tipo].length - 1),
    }),
    solve: solveKey,
    buggy: comExplicacoes(BUGGY, EXPLICA),

    variantDifficulty(p) {
      const fontes: DifficultySourceCode[] = [];
      if (p.n >= 13) fontes.push('non-round-numbers');
      if (CENARIOS[p.tipo][p.cenario]?.texto.includes('trajetória'))
        fontes.push('reading-load.high');
      return fontes;
    },

    build(p, key, distractors, rng) {
      const interaction = assembleSelectOne<number>({
        structure: 'E1',
        key,
        distractors,
        render,
        // Texto, não número: as alternativas vão embaralhadas (ver o comentário do módulo).
        raw: (v) => g(v),
        rng,
      });
      const cen = CENARIOS[p.tipo][p.cenario] as Cenario;
      const s = suporte(p);
      return {
        stem: [txt(cen.texto.replace('{n}', String(p.n))), br, ...s.convite],
        comando: [txt(cen.pergunta.replace('{n}', String(p.n)))],
        stimuli: [s.estimulo],
        interaction,
        rationale: resolucao(p, key),
        declared: p.tipo === 'soma-ext' ? 'facil' : p.tipo === 'soma-int' ? 'media' : 'media',
      };
    },

    validate(_b, p) {
      if ((p.tipo === 'soma-int' || p.tipo === 'int-cada') && p.n <= EXPLORADOR_MAX) {
        return 'o polígono da pergunta está dentro do alcance do explorador';
      }
      return null;
    },
  };
}

export const somaAngulosInternos = criar('soma-int');
export const anguloInterno = criar('int-cada');
export const somaAngulosExternos = criar('soma-ext');
export const anguloExterno = criar('ext-cada');
